import { ApolloError } from '@apollo/client';
import { useListStringGroupVersionsLazyQuery } from 'src/generated/graphql';
import { useEffect } from 'react';
import { StringGroupVersionSummary } from '../types';

interface UseVersionHistoryResult {
  data: StringGroupVersionSummary[] | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Lazily fetches version history for a string group. Only loads data when
 * `enabled` is true (i.e., the version history panel is open).
 * Uses a lazy query to avoid fetching on mount — only fires when needed.
 */
export function useVersionHistory(
  enabled: boolean,
  namespaceId?: string,
  stringGroupId?: string,
): UseVersionHistoryResult {
  const [fetchVersions, { data, loading, error }] =
    useListStringGroupVersionsLazyQuery({
      fetchPolicy: 'cache-and-network',
    });

  useEffect(() => {
    if (enabled && namespaceId && stringGroupId) {
      fetchVersions({
        variables: {
          namespaceId,
          stringGroupId,
          sortOrder: 'DESC',
          maxResults: 25,
        },
      });
    }
  }, [enabled, namespaceId, stringGroupId, fetchVersions]);

  const mapped: StringGroupVersionSummary[] | null = data
    ? data.listStringGroupVersions.versions.map((v) => ({
        // Defensive coercion, so downstream strict-equality version checks
        // hold even if a string reaches us.
        version: Number(v.version),
        translationStatus: v.translationStatus,
        stringCount: v.stringCount,
        updatedOn: v.updatedOn ?? 0,
        updatedBy: v.updatedBy ?? '',
        changeSummary: v.changeSummary
          ? {
              addedCount: v.changeSummary.addedCount,
              editedCount: v.changeSummary.editedCount,
              deletedCount: v.changeSummary.deletedCount,
              description: formatChangeSummary(v.version, v.changeSummary),
            }
          : undefined,
      }))
    : null;

  return {
    data: mapped,
    loading,
    error: toError(error),
  };
}

function formatChangeSummary(
  version: number,
  summary: {
    addedCount: number;
    editedCount: number;
    deletedCount: number;
  },
): string {
  if (
    version === 1 &&
    summary.addedCount > 0 &&
    summary.editedCount === 0 &&
    summary.deletedCount === 0
  ) {
    return `${summary.addedCount} string${
      summary.addedCount > 1 ? 's' : ''
    } created (initial)`;
  }

  const parts: string[] = [];
  if (summary.addedCount > 0) {
    parts.push(
      `${summary.addedCount} new string${
        summary.addedCount > 1 ? 's' : ''
      } added`,
    );
  }
  if (summary.editedCount > 0) {
    parts.push(
      `${summary.editedCount} string${summary.editedCount > 1 ? 's' : ''} edited`,
    );
  }
  if (summary.deletedCount > 0) {
    parts.push(
      `${summary.deletedCount} string${summary.deletedCount > 1 ? 's' : ''} deleted`,
    );
  }
  return parts.join(', ') || 'No changes';
}

function toError(error: ApolloError | undefined): Error | null {
  if (!error) return null;
  return new Error(error.message);
}
